import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { COLORS } from "../../assets/theme";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CollapsibleSection from "../../components/common/CollapsibleSection";
import CustomButton from "../../components/common/CustomButton";
import CustomRadio from "../../components/CustomRadio";
import config from "../../config.json";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
  pageAsOptions,
} from "../../utils/commonDataUtils";
import { formatNumberWithCommas } from "../../utils/helpers";
import {
  CdrStatus,
  IMonitor,
  useMonitorQuery,
} from "../../services/LiveMonitorService";
import { useProviderOptionQuery } from "../../services/ProviderService";
import { fetchClients } from "../../services/ClientService";
import DateFilterButtons from "../../components/DateFilterButtons";
import { DlrStatus } from "../../services/MessageReSubmissionService";
import {
  fetchProfiles,
  useProfilesQuery,
} from "../../services/ProfilesService";
import { useUsersByEmailQuery } from "../../services/UsersService";
import LiveMonitorTable from "../../components/LiveMonitorTable";
import ConditionValuesField from "../../components/ConditionValuesField";
import { fetchCurrency } from "../../services/CurrencyService";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { fetchProviders } from "../../services/ProviderService";
import { getRealmRoles } from "../../services/ServicesHelpers";
import CostBreakdownWithDialog from "../../components/CostBreakdownWithDialog";
const LIVE_POLLING_INTERVAL_MS = 5000;

const FILTER_GRID_COLUMNS = {
  xs: "1fr",
  sm: "repeat(2, minmax(0, 1fr))",
  md: "repeat(4, minmax(0, 1fr))",
};

const TWO_GRID_COLUMNS_WIDTH = "calc(50% - 6px)";

const LiveMonitorPage: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [appliedFilters, setAppliedFilters] = useState<
    Params<
      IMonitor &
        DateRange & {
          // profileId: string;
          profileType: string;
          connectionProfileId: string;
          vendorId: string;
          destinationCountryId: string;
          destinationOperatorId: string;
          deliveryStat: string;
          status: string;
          messageText: string;
          terminatorId: string;
          targetUserId?: string;
        }
    >
  >({});

  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [clientOptions, setClientOptions] = useState<Option[]>([]);
  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [conditionStatuses, setConditionStatuses] = useState<
    {
      conditionType?: string;
      status?: string;
      field?: string;
      value?: string;
    }[]
  >([{ conditionType: "Equals", status: undefined }]);
  const [filters, setFilters] = useState<
    Params<
      IMonitor & {
        profileId: string;
        groupBySenderId: boolean;
        profileType: string;
        connectionProfileId: string;
        vendorId: string;
        destinationCountryId: string;
        destinationOperatorId: string;
        deliveryStat: string;
        status: string;
        messageText: string;
        terminatorId: string;
        search: string;
        clientProfileId?: string;
        vendorProfileId?: string;
        targetUserId?: string;
      }
    >
  >({
    profileId: "",
    profileType: "CLIENT",
    connectionProfileId: "",
    vendorId: "",
    destinationCountryId: "",
    destinationOperatorId: "",
    deliveryStat: "",
    status: "",
    messageText: "",
    terminatorId: "",
    search: "",
    clientProfileId: "",
    vendorProfileId: "",
    targetUserId: undefined,
  });

  const [selectedOption, setSelectedOption] = useState<string>("CLIENT");

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>("");
  const normalizedRoles = getRealmRoles().map((role) =>
    role.toUpperCase().replace(/[_\s]/g, ""),
  );
  const canFilterByEmployee =
    normalizedRoles.includes("SUPER") ||
    normalizedRoles.includes("ACCESS_ALL_ACCOUNTS");

  // Live mode state
  const [isLiveMode, setIsLiveMode] = useState<boolean>(false);
  const livePollingRef = useRef<NodeJS.Timeout | null>(null);
  const [nextRefreshAt, setNextRefreshAt] = useState<number | null>(null);
  const [groupByProfile, setGroupByProfile] = useState<boolean>(false);
  const [selectedDateLabel, setSelectedDateLabel] = useState<string>("");
  const preLiveRangeRef = useRef<{
    fromDate: Date | null;
    toDate: Date | null;
    dateFilter: string;
    dateLabel: string;
  } | null>(null);

  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const scrollContainer = pageRef.current?.closest("main");
    const filtersEl = filtersRef.current;
    if (!scrollContainer || !filtersEl) return;
    const handleScroll = () => {
      const filtersBottom = filtersEl.getBoundingClientRect().bottom;
      const containerTop = scrollContainer.getBoundingClientRect().top;
      if (filtersBottom <= containerTop) {
        setFiltersExpanded(false);
      }
    };
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  const [profileMap, setProfileMap] = useState<Record<string, any>>({});
  const [currencyMap, setCurrencyMap] = useState<Record<string, string>>({});

  const [tableData, setTableData] = useState<IMonitor[]>([]);

  const [isQueryEnabled, setIsQueryEnabled] = useState(false);

  const {
    data: monitorData,
    isLoading: isMonitorLoading,
    isFetching: isMonitorFetching,
  } = useMonitorQuery(appliedFilters, isQueryEnabled);

  const CdrStatusOptions = Object.entries(CdrStatus)
    .filter(([key, value]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key,
      value: String(key),
    }));

  useEffect(() => {
    const fetchProfilesAndCurrencies = async () => {
      if (!tableData || tableData.length === 0) return;

      const uniqueProfiles = Array.from(
        new Set(tableData.map((row) => row.profile).filter(Boolean)),
      );

      const profilesPages = await Promise.all(
        uniqueProfiles.map((name) => fetchProfiles({ name })),
      );
      const profiles = profilesPages
        .map((page) =>
          page.content && page.content.length > 0 ? page.content[0] : null,
        )
        .filter(Boolean);

      const newProfileMap: Record<string, any> = {};
      profiles.forEach((profile) => {
        if (profile) {
          newProfileMap[profile.name] = profile;
        }
      });
      setProfileMap(newProfileMap);

      const uniqueCurrencyIds = Array.from(
        new Set(
          profiles
            .map((p) => p && p.currencyId)
            .filter((id): id is number => id !== null && id !== undefined),
        ),
      );

      const currencies = await Promise.all(
        uniqueCurrencyIds.map((id) => fetchCurrency(id)),
      );

      const newCurrencyMap: Record<string, string> = {};
      currencies.forEach((currency, idx) => {
        const currencyId = uniqueCurrencyIds[idx];
        if (currency && currencyId !== null && currencyId !== undefined) {
          newCurrencyMap[currencyId] = currency.name;
        }
      });
      setCurrencyMap(newCurrencyMap);
    };

    fetchProfilesAndCurrencies();
  }, [tableData]);
  useEffect(() => {
    if (monitorData) {
      setTableData(
        monitorData.map((monitor) => ({
          ...monitor,
          clientId: monitor.id ?? "",
        })),
      );
    }
  }, [monitorData]);

  const dlrStatusOptions = Object.entries(DlrStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key]) => ({
      label: key,
      value: key,
    }));

  const { data: profileOption } = useProfilesQuery();
  const { data: providerOptions = [] } = useProviderOptionQuery();
  const { data: fetchUsersByEmail = [] } = useUsersByEmailQuery();

  const profileOptions = useMemo(
    () => profileOption?.profileOptions ?? [],
    [profileOption],
  );

  useEffect(() => {
    setUserOptions(fetchUsersByEmail ?? []);
  }, [fetchUsersByEmail]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const clientPage = await fetchClients({
          size: config.MAX_PAGE_SIZE,
        });
        setClientOptions(pageAsOptions(clientPage));
        const vendorPage = await fetchProviders({
          size: config.MAX_PAGE_SIZE,
        });
        setProvidersOptions(pageAsOptions(vendorPage));

        setCountriesOptions(await loadCountriesOptions());
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, [profileOption]);

  const transformConditions = (
    conditions: {
      field?: string;
      conditionType?: string;
      status?: string;
      value?: string;
    }[],
  ): Record<string, string[]> =>
    conditions.reduce(
      (acc, { field, conditionType, status, value }) => {
        if (field && conditionType) {
          const typeCamel = conditionType.replace(/_([a-z])/g, (_, letter) =>
            letter.toUpperCase(),
          );
          const key = `${field}${typeCamel}`;
          if (!acc[key]) {
            acc[key] = [];
          }
          if (
            acc[key].length === 0 &&
            (status === undefined || status === "")
          ) {
            acc[key].push(value ?? "");
          } else {
            acc[key].push(status ?? "");
          }
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );

  const logConditionStatuses = (
    statuses?: {
      field?: string;
      conditionType?: string;
      status?: string;
      label?: string;
      value?: string;
    }[],
  ) => {
    if (!statuses) {
      console.log("No condition statuses provided");
      return;
    }

    const result: Record<string, string[]> = {};

    statuses.forEach((condition, idx) => {
      const { field, conditionType, status, value } = condition;
      const typeCamel = conditionType
        ? conditionType.replace(/_([a-z])/g, (_, letter) => letter)
        : "";
      const combinedKey = field && typeCamel ? `${field}${typeCamel}` : "";

      if (!combinedKey) return;

      if (!result[combinedKey]) {
        result[combinedKey] = [];
      }

      if (idx === 0 && (status === undefined || status === "")) {
        result[combinedKey].push(value ?? "");
      } else {
        result[combinedKey].push(status ?? "");
      }
    });
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev: any) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    // A manual search wins over Live: stop polling so the next tick can't
    // overwrite the range being searched.
    if (isLiveMode) {
      preLiveRangeRef.current = null;
      stopLiveMode();
    }
    const conditionParams = transformConditions(conditionStatuses ?? []);
    setAppliedFilters((prev) => ({
      ...filters,
      ...conditionParams,
      status: filters.status,
      search: prev.search ?? "",
      fromDate: fromDate ? fromDate : undefined,
      toDate: toDate ? toDate : undefined,
      _forceRefresh: Date.now(),
    }));
    setIsQueryEnabled(true);
  };
  const handleCountryChange = async (
    value: string | number | (string | number)[],
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;
    if (selectedCountry) {
      try {
        const destinationCountryId = Number(selectedCountry);
        if (isNaN(destinationCountryId)) throw new Error("Invalid country ID");
        setIsOperatorDisabled(true);
        const ops = await loadOperatorsByCountry(destinationCountryId);
        setOperatorsByCountry(ops);
      } catch (error: any) {
        console.error("Error fetching operators:", error);
        setOperatorsByCountry([]);
      } finally {
        setIsOperatorDisabled(false);
      }
    } else {
      setOperatorsByCountry([]);
      setIsOperatorDisabled(false);
    }
  };

  // Function to fetch data for the last 60 seconds (Live mode)
  const fetchLiveData = useCallback(() => {
    const now = new Date();
    const sixtySecondsAgo = new Date(now.getTime() - 60 * 1000);

    setFromDate(sixtySecondsAgo);
    setToDate(now);

    const conditionParams = transformConditions(conditionStatuses ?? []);
    setAppliedFilters((prev) => ({
      ...filters,
      ...conditionParams,
      status: filters.status,
      search: prev.search ?? "",
      fromDate: sixtySecondsAgo,
      toDate: now,
      _forceRefresh: Date.now(),
    }));
    setNextRefreshAt(Date.now() + LIVE_POLLING_INTERVAL_MS);
    setIsQueryEnabled(true);
  }, [filters, conditionStatuses]);

  const fetchLiveDataRef = useRef(fetchLiveData);
  useEffect(() => {
    fetchLiveDataRef.current = fetchLiveData;
  }, [fetchLiveData]);

  const stopLiveMode = useCallback(() => {
    if (livePollingRef.current) {
      clearInterval(livePollingRef.current);
      livePollingRef.current = null;
    }
    setIsLiveMode(false);
    setNextRefreshAt(null);

    const previous = preLiveRangeRef.current;
    preLiveRangeRef.current = null;
    if (previous) {
      setFromDate(previous.fromDate);
      setToDate(previous.toDate);
      setSelectedDateFilter(previous.dateFilter);
      setSelectedDateLabel(previous.dateLabel);
    }
  }, []);

  // Handle Live mode toggle
  const handleLiveModeChange = (checked: boolean) => {
    if (!checked) {
      stopLiveMode();
      return;
    }

    setIsLiveMode(true);

    // Clear any existing interval
    if (livePollingRef.current) {
      clearInterval(livePollingRef.current);
    }

    // Remember what the user had selected so it can be restored on exit
    preLiveRangeRef.current = {
      fromDate,
      toDate,
      dateFilter: selectedDateFilter,
      dateLabel: selectedDateLabel,
    };

    // Clear the selected date filter button since we're in live mode
    setSelectedDateFilter("");
    setSelectedDateLabel("");

    // Immediately fetch live data
    fetchLiveData();

    // Set up polling interval
    livePollingRef.current = setInterval(() => {
      fetchLiveDataRef.current();
    }, LIVE_POLLING_INTERVAL_MS);
  };

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (livePollingRef.current) {
        clearInterval(livePollingRef.current);
      }
    };
  }, []);

  // Disable live mode when user manually changes date filters
  useEffect(() => {
    if (selectedDateFilter && isLiveMode) {
      preLiveRangeRef.current = null;
      stopLiveMode();
    }
  }, [selectedDateFilter, isLiveMode, stopLiveMode]);

  const autoCompletesBase: IAutoCompleteProps[] = [
    {
      label: "Profile",
      value:
        selectedOption === "CLIENT"
          ? (filters.clientProfileId ?? "")
          : selectedOption === "VENDOR"
            ? (filters.vendorProfileId ?? "")
            : "",
      options: profileOptions,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProfile = profileOptions.find(
          (option) => option.value === value,
        );
        const profileId = selectedProfile
          ? selectedProfile.value.toString()
          : "";
        setFilters((prev: any) => {
          if (selectedOption === "CLIENT") {
            return {
              ...prev,
              clientProfileId: profileId,
              vendorProfileId: undefined,
            };
          } else if (selectedOption === "VENDOR") {
            return {
              ...prev,
              vendorProfileId: profileId,
              clientProfileId: undefined,
            };
          } else {
            return prev;
          }
        });
      },
      clearable: true,
    },
    {
      label: "Customer",
      value: filters.connectionProfileId ?? "",
      options: clientOptions,
      clearable: true,
      placeholder: "Select Client",
      onChange: (value) => {
        const selected = clientOptions.find((option) => option.value === value);
        setFilters((prev: any) => ({
          ...prev,
          connectionProfileId: selected ? String(selected.value) : undefined,
        }));
      },
    },
    {
      label: "Provider",
      value: filters.vendorId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value) => {
        const selected = providersOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev: any) => ({
          ...prev,
          vendorId: selected ? String(selected.value) : undefined,
        }));
      },
    },
    ...(canFilterByEmployee
      ? [
          {
            label: "Employee",
            value: filters.targetUserId ?? "",
            options: userOptions,
            clearable: true,
            placeholder: "Select User",
            onChange: (value) => {
              const selected = userOptions.find(
                (option) => option.value === value,
              );
              setFilters((prev: any) => ({
                ...prev,
                targetUserId: selected ? String(selected.value) : undefined,
              }));
            },
          } as IAutoCompleteProps,
        ]
      : []),
    {
      label: "Country",
      value: filters.destinationCountryId ?? "",
      options: countriesOptions,
      clearable: true,
      placeholder: "Select Country",
      onChange: async (value) => {
        const selected = countriesOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev: any) => ({
          ...prev,
          destinationCountryId: selected ? String(selected.value) : "",
          destinationOperatorId: "",
        }));
        if (selected) {
          try {
            setIsOperatorDisabled(true);
            const ops = await loadOperatorsByCountry(Number(selected.value));
            setOperatorsByCountry(ops);
          } catch (error: any) {
            console.error("Error loading operators:", error);
            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
        try {
          await handleCountryChange(value);
        } catch (error: any) {
          console.error("Error in handleCountryChange:", error);
        }
      },
    },
    {
      label: "Operator",
      value: filters.destinationOperatorId ?? "",
      options: operatorsByCountry,
      clearable: true,
      disabled: isOperatorDisabled,
      placeholder: "Select Operator",
      onChange: (value) => {
        const selected = operatorsByCountry.find(
          (option) => option.value === value,
        );
        setFilters((prev: any) => ({
          ...prev,
          destinationOperatorId: selected ? String(selected.value) : "",
        }));
      },
    },
    {
      label: "Status",
      value: filters.status ?? "",
      options: CdrStatusOptions,
      clearable: true,
      placeholder: "Select Status",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedStatus = Object.keys(CdrStatus).find(
          (key) => key === value,
        );
        setFilters((prev) => ({
          ...prev,
          status: selectedStatus || undefined,
        }));
      },
    },
    {
      label: "Delivery Status",
      value: filters.deliveryStat ?? "",
      options: dlrStatusOptions,
      clearable: true,
      placeholder: "Select delivery Status",
      onChange: (value) => {
        const selected = Object.keys(DlrStatus).find((key) => key === value);
        setFilters((prev: any) => ({
          ...prev,
          deliveryStat: selected || "",
        }));
      },
    },
  ];

  const fields = useMemo<IField<IMonitor>[]>(
    () => [
      { key: "name", header: "Name" },
      {
        key: "count",
        header: "Count",
        transform: (t: IMonitor) => formatNumberWithCommas(t.count),
      },
      {
        key: "delivered",
        header: "Delivered",
        transform: (t: IMonitor) => formatNumberWithCommas(t.delivered),
      },
      {
        key: "deliveryPercentage",
        header: "Delivery%",
        transform: async (t: IMonitor) => {
          if (t.count) {
            const percentage = (t.delivered / t.count) * 100;
            return percentage.toFixed(2) + "%";
          }
          return "0%";
        },
      },
      {
        key: "totalCost",
        header: "Total Converted Cost",
        sortValue: (t: IMonitor) => {
          const parts = [
            t.totalConvertedRoutingCost,
            t.totalConvertedMnpCost,
            t.totalConvertedHlrCost,
          ].filter((v) => v !== null && v !== undefined);
          return parts.length
            ? parts.reduce((sum: number, v) => sum + Number(v), 0)
            : null;
        },
        transform: (t: IMonitor) => {
          const convertedRoutingCost = t.totalConvertedRoutingCost ?? 0;
          const convertedMnpCost = t.totalConvertedMnpCost;
          const convertedHlrCost = t.totalConvertedHlrCost;

          const profile = t.profile ? profileMap[t.profile] : undefined;
          const convertedCurrency = profile?.currencyId
            ? currencyMap[profile.currencyId]
            : undefined;

          return (
            <CostBreakdownWithDialog
              routingCost={convertedRoutingCost}
              routingCostCurrency={convertedCurrency}
              convertedCostCurrency={convertedCurrency}
              {...(convertedMnpCost !== undefined && convertedMnpCost !== null
                ? {
                    mnpCost: convertedMnpCost,
                    mnpCostCurrency: convertedCurrency,
                  }
                : {})}
              {...(convertedHlrCost !== undefined && convertedHlrCost !== null
                ? {
                    hlrCost: convertedHlrCost,
                    hlrCostCurrency: convertedCurrency,
                  }
                : {})}
            />
          );
        },
      },
      {
        key: "totalRate",
        header: "Total Rate",
        transform: (t: IMonitor) => {
          const value = Number(t.totalRate);
          return isNaN(value)
            ? "0"
            : formatNumberWithCommas(value, { maximumFractionDigits: 6 });
        },
      },
      {
        key: "totalProfit",
        header: "Profit",
        transform: (t: IMonitor) => {
          const value = Number(t.totalProfit);
          return isNaN(value)
            ? "0"
            : formatNumberWithCommas(value, { maximumFractionDigits: 6 });
        },
      },
      {
        key: "profile",
        header: "Currency",
        transform: (t: IMonitor) => {
          if (!t.profile) return "";
          const profile = profileMap[t.profile];
          if (!profile) return "";
          return currencyMap[profile.currencyId] || "";
        },
      },
      { key: "employeeName", header: "Employee" },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [profileMap, currencyMap],
  );

  const getRowCurrency = useCallback(
    (t: IMonitor) => {
      const profile = t.profile ? profileMap[t.profile] : undefined;
      return profile?.currencyId ? currencyMap[profile.currencyId] : undefined;
    },
    [profileMap, currencyMap],
  );

  const handleDateFilterSelect = (range: string, label: string = "") => {
    setSelectedDateFilter(range);
    setSelectedDateLabel(range ? label : "");
    if (range) {
      const [startStr, endStr] = range.split("_");
      setFromDate(new Date(startStr));
      setToDate(new Date(endStr));
    } else {
      setFromDate(null);
      setToDate(null);
    }
  };
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getUTCFullYear();
    const mm = String(today.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(today.getUTCDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;
    const tomorrow = new Date(today);
    tomorrow.setUTCDate(today.getUTCDate() + 1);
    const yyyy2 = tomorrow.getUTCFullYear();
    const mm2 = String(tomorrow.getUTCMonth() + 1).padStart(2, "0");
    const dd2 = String(tomorrow.getUTCDate()).padStart(2, "0");
    const tomorrowStr = `${yyyy2}-${mm2}-${dd2}`;
    handleDateFilterSelect(`${todayStr}_${tomorrowStr}`, "Today");
  }, []);

  return (
    <Box padding={isSmallScreen ? 2 : 3} ref={pageRef}>
      <Box ref={filtersRef}>
        <CollapsibleSection
          title="Filters"
          icon={<FilterAltIcon fontSize="small" sx={{ color: COLORS.GOLD }} />}
          expanded={filtersExpanded}
          onExpandedChange={setFiltersExpanded}
          sx={{ mb: 3 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2 },
              p: { xs: 1.5, sm: 2.5 },
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              <DateFilterButtons
                onSelect={handleDateFilterSelect}
                selectedOption={selectedDateLabel}
                fromDate={fromDate}
                toDate={toDate}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-start",
                rowGap: 1.5,
                columnGap: 3,
              }}
            >
              <CustomDateRangePicker
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                width={TWO_GRID_COLUMNS_WIDTH}
                minWidth={TWO_GRID_COLUMNS_WIDTH}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  columnGap: 2,
                  mt: 2.5,
                }}
              >
                <FormControlLabel
                  control={
                    <CustomRadio
                      checked={selectedOption === "CLIENT"}
                      value="CLIENT"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const newValue = event.target.value;
                        setSelectedOption(newValue);
                        setFilters((prev: any) => ({
                          ...prev,
                          profileType: newValue,
                          clientProfileId:
                            prev.vendorProfileId ?? prev.clientProfileId,
                          vendorProfileId: undefined,
                        }));
                      }}
                    />
                  }
                  label="Client"
                  sx={{ ml: 0, mr: 0, mb: 0, whiteSpace: "nowrap" }}
                />
                <FormControlLabel
                  control={
                    <CustomRadio
                      checked={selectedOption === "VENDOR"}
                      value="VENDOR"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const newValue = event.target.value;
                        setSelectedOption(newValue);
                        setFilters((prev: any) => ({
                          ...prev,
                          profileType: newValue,
                          vendorProfileId:
                            prev.clientProfileId ?? prev.vendorProfileId,
                          clientProfileId: undefined,
                        }));
                      }}
                    />
                  }
                  label="Provider"
                  sx={{ ml: 0, mr: 0, mb: 0, whiteSpace: "nowrap" }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: FILTER_GRID_COLUMNS,
                gap: 1.5,
              }}
            >
              {autoCompletesBase.map((field) => (
                <AutoComplete
                  key={field.label}
                  {...field}
                  minWidth="0px"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: COLORS.iconColor,
                    },
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {conditionStatuses && conditionStatuses.length > 0 && (
                <Box
                  sx={{
                    flex: "0 1 auto",
                    minWidth: TWO_GRID_COLUMNS_WIDTH,
                    maxWidth: "100%",
                  }}
                >
                  <ConditionValuesField
                    whiteBackground
                    notIfCondition
                    conditionTypeOptions={[
                      { label: "Contains", value: "Contains" },
                      { label: "Equals", value: "Equals" },
                    ]}
                    label="Sender ID"
                    type="text"
                    labelOptions={[
                      { label: "SID", value: "originalSource" },
                      {
                        label: "Translated SID",
                        value: "postTranslatedSource",
                      },
                      { label: "Content", value: "originalMessageText" },
                      { label: "Translated Content", value: "messageText" },
                    ]}
                    onLabelChange={(index, value) => {
                      setConditionStatuses((prev) => {
                        const updatedStatuses = prev.map((c, i) =>
                          i === index ? { ...c, field: value } : c,
                        );
                        logConditionStatuses(updatedStatuses);
                        return updatedStatuses;
                      });
                    }}
                    conditionStatuses={conditionStatuses}
                    handleConditionChange={(index, field, status) => {
                      setConditionStatuses((prev) => {
                        const updatedStatuses = prev.map((c, i) =>
                          i === index ? { ...c, [field]: status } : c,
                        );
                        logConditionStatuses(updatedStatuses);
                        return updatedStatuses;
                      });
                    }}
                    handleAddCondition={() => {
                      setConditionStatuses((prev) => [
                        ...prev,
                        { conditionType: "", status: "" },
                      ]);
                    }}
                    handleRemoveCondition={(index) => {
                      setConditionStatuses((prev) =>
                        prev.filter((_, i) => i !== index),
                      );
                    }}
                  />
                </Box>
              )}
              <CustomButton
                label={
                  isMonitorFetching && !isLiveMode ? "Searching..." : "Search"
                }
                variant="contained"
                size="small"
                bgColor={COLORS.GOLD}
                textColor={COLORS.iconColor}
                borderRadius="10px"
                startIcon={<SearchIcon fontSize="small" />}
                onClick={handleSearchClick}
                permission="GET_ALL_MONITORS"
                disabled={isMonitorFetching && !isLiveMode}
                sx={{ flex: "0 0 auto" }}
              />
            </Box>
          </Box>
        </CollapsibleSection>
      </Box>

      <Box sx={{ mt: 3 }}>
        <LiveMonitorTable<IMonitor>
          data={tableData}
          filters={appliedFilters}
          fields={fields}
          search={appliedFilters.search}
          setSearch={handleSearchChange}
          searchOnEnter
          profileIdCheck={groupByProfile}
          initialPageSize={5}
          isLoading={Boolean(isMonitorLoading) && tableData.length === 0}
          isRefreshing={Boolean(isMonitorFetching) && tableData.length > 0}
          nextRefreshAt={isLiveMode ? nextRefreshAt : null}
          usedBy="live-monitor"
          getRowCurrency={getRowCurrency}
          headerExtra={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.25, sm: 1.5 },
                flexWrap: "nowrap",
                minWidth: 0,
                flexGrow: { xs: 1, sm: 0 },
                "& .MuiFormControlLabel-root": {
                  minHeight: { xs: 44, sm: "auto" },
                },
                "& .MuiFormControlLabel-label": {
                  fontSize: { xs: "1rem", sm: "1rem" },
                },
              }}
            >
              <Tooltip title="Group accounts under their profile" arrow>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={groupByProfile}
                      onChange={(event) =>
                        setGroupByProfile(event.target.checked)
                      }
                      sx={{
                        color: COLORS.GOLD,
                        "&.Mui-checked": { color: COLORS.GOLD },
                      }}
                    />
                  }
                  label={isSmallScreen ? "Group" : "Group by profile"}
                  sx={{ whiteSpace: "nowrap", ml: 0, mr: 0 }}
                />
              </Tooltip>
              <Tooltip
                title={`Auto-refreshes every ${LIVE_POLLING_INTERVAL_MS / 1000} seconds, showing the last 60 seconds of traffic`}
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isLiveMode}
                      onChange={(event) =>
                        handleLiveModeChange(event.target.checked)
                      }
                      sx={{
                        color: COLORS.GOLD,
                        "&.Mui-checked": { color: COLORS.GOLD },
                      }}
                    />
                  }
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      Live
                      {isLiveMode && (
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: COLORS.GOLD,
                            animation: "liveDotPulse 1.6s ease-in-out infinite",
                            "@keyframes liveDotPulse": {
                              "0%, 100%": { opacity: 1, transform: "scale(1)" },
                              "50%": { opacity: 0.35, transform: "scale(0.8)" },
                            },
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{ whiteSpace: "nowrap", ml: 0, mr: 0 }}
                />
              </Tooltip>
            </Box>
          }
        />
      </Box>
    </Box>
  );
};

export default LiveMonitorPage;
